import { useState, useEffect, useRef } from 'react';
import { HiOutlineX, HiOutlinePlus, HiOutlineCheck, HiOutlineSearch, HiOutlineFolder } from 'react-icons/hi';
import { fetchAllLists, addToList, bulkAddToList, createList, removeFromList, ListSummary } from '../../services/listService';
import toast from 'react-hot-toast';
import './AddToListDialog.css';

interface BulkStoreItem {
  poiId: string;
  storeName?: string;
  brandName?: string;
}

interface AddToListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'brand' | 'store';
  brandName?: string;
  poiId?: string;
  storeName?: string;
  /** When provided, saves multiple stores at once instead of a single item */
  bulkStores?: BulkStoreItem[];
}

export default function AddToListDialog({
  isOpen,
  onClose,
  type,
  brandName,
  poiId,
  storeName,
  bulkStores,
}: AddToListDialogProps) {
  const isBulk = bulkStores && bulkStores.length > 0;
  const [lists, setLists] = useState<ListSummary[]>([]);
  const [selectedLists, setSelectedLists] = useState<Set<string>>(new Set());
  const [initialSelectedLists, setInitialSelectedLists] = useState<Set<string>>(new Set());
  const [newListName, setNewListName] = useState('');
  const [showNewListInput, setShowNewListInput] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const newListInputRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedLists(new Set());
      setNewListName('');
      setShowNewListInput(false);
      setSearchTerm('');
      loadLists();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (showNewListInput && newListInputRef.current) {
      newListInputRef.current.focus();
    }
  }, [showNewListInput]);

  const loadLists = async () => {
    setLoading(true);
    try {
      const { myLists } = await fetchAllLists();
      const data = myLists.filter((l: ListSummary) => l.list_type === type);
      setLists(data);

      // Pre-check lists that already contain this item
      const alreadyIn = new Set<string>();
      data.forEach((list: any) => {
        const exists = list.items.some((item: any) => {
          if (type === 'brand') return item.type === 'brand' && item.brand_name === brandName;
          return item.type === 'store' && item.poi_id === poiId;
        });
        if (exists) alreadyIn.add(list.list_name);
      });
      setSelectedLists(alreadyIn);
      setInitialSelectedLists(new Set(alreadyIn));
    } catch {
      // Error handled in service
    } finally {
      setLoading(false);
    }
  };

  const toggleList = (listName: string) => {
    setSelectedLists(prev => {
      const next = new Set(prev);
      if (next.has(listName)) next.delete(listName);
      else next.add(listName);
      return next;
    });
  };

  const handleCreateNewList = async () => {
    const name = newListName.trim();
    if (!name) return;
    if (lists.some(l => l.list_name.toLowerCase() === name.toLowerCase())) {
      toast.error('A list with this name already exists.');
      return;
    }

    const result = await createList(name, type);
    if (result.success) {
      setLists(prev => [{ list_name: name, description: null, list_type: type, count: 0, items: [] }, ...prev]);
      setSelectedLists(prev => new Set(prev).add(name));
      setNewListName('');
      setShowNewListInput(false);
    } else {
      toast.error(result.error || 'Failed to create list');
    }
  };

  const handleSave = async () => {
    // Build the final set of selected lists (copy current state)
    const finalSelected = new Set(selectedLists);

    // If the user typed a new list name but forgot to click the tick, create it automatically
    const pendingName = newListName.trim();
    if (showNewListInput && pendingName) {
      if (lists.some(l => l.list_name.toLowerCase() === pendingName.toLowerCase())) {
        toast.error('A list with this name already exists.');
        return;
      }
      const createResult = await createList(pendingName, type);
      if (createResult.success) {
        setLists(prev => [{ list_name: pendingName, description: null, list_type: type, count: 0, items: [] }, ...prev]);
        setNewListName('');
        setShowNewListInput(false);
        finalSelected.add(pendingName);
        setSelectedLists(new Set(finalSelected));
      } else {
        toast.error(createResult.error || 'Failed to create list');
        return;
      }
    }

    // Determine what changed
    const listsToAdd = new Set<string>();
    const listsToRemove = new Set<string>();

    finalSelected.forEach(listName => {
      if (!initialSelectedLists.has(listName)) listsToAdd.add(listName);
    });
    initialSelectedLists.forEach(listName => {
      if (!finalSelected.has(listName)) listsToRemove.add(listName);
    });

    if (listsToAdd.size === 0 && listsToRemove.size === 0) {
      toast('No changes to save.', { icon: 'ℹ️' });
      onClose();
      return;
    }

    setSaving(true);

    // Handle removals
    let removedCount = 0;
    for (const listName of Array.from(listsToRemove)) {
      const existingList = lists.find(l => l.list_name === listName);
      if (existingList) {
        const matchingItem = existingList.items.find((item: any) => {
          if (type === 'brand') return item.type === 'brand' && item.brand_name === brandName;
          return item.type === 'store' && item.poi_id === poiId;
        });
        if (matchingItem) {
          const result = await removeFromList(matchingItem.id);
          if (result.success) removedCount++;
        }
      }
    }

    // Handle additions
    if (isBulk && listsToAdd.size > 0) {
      const items = bulkStores!.map(store => ({
        poiId: store.poiId,
        brandName: store.brandName || brandName || null,
      }));
      const result = await bulkAddToList(Array.from(listsToAdd), items, 'store');

      setSaving(false);

      if (result.success && result.insertedCount > 0) {
        toast.success(`Added ${result.insertedCount} store${result.insertedCount !== 1 ? 's' : ''} to ${listsToAdd.size} list${listsToAdd.size !== 1 ? 's' : ''}.`);
      } else if (result.success && result.insertedCount === 0 && removedCount === 0) {
        toast('All selected stores already exist in the selected list(s).', { icon: 'ℹ️' });
      }
      if (removedCount > 0) {
        toast.success(`Removed from ${removedCount} list${removedCount > 1 ? 's' : ''}.`);
      }
    } else {
      let successCount = 0;

      for (const listName of Array.from(listsToAdd)) {
        const result = await addToList(listName, type, poiId, brandName);
        if (result.success) successCount++;
      }

      setSaving(false);

      if (successCount > 0) {
        const label = type === 'brand' ? brandName : (storeName || poiId);
        toast.success(`Added "${label}" to ${successCount} list${successCount > 1 ? 's' : ''}.`);
      }
      if (removedCount > 0) {
        toast.success(`Removed from ${removedCount} list${removedCount > 1 ? 's' : ''}.`);
      }
    }

    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  if (!isOpen) return null;

  const displayName = isBulk
    ? `${bulkStores!.length} store${bulkStores!.length !== 1 ? 's' : ''} selected`
    : type === 'brand' ? brandName : (storeName || poiId || 'Store');

  const filteredLists = searchTerm
    ? lists.filter(l => l.list_name.toLowerCase().includes(searchTerm.toLowerCase()))
    : lists;

  return (
    <div className="atl-backdrop" ref={backdropRef} onClick={handleBackdropClick}>
      <div className="atl-dialog">
        <div className="atl-header">
          <div className="atl-header-text">
            <h3 className="atl-title">Save to List</h3>
            <span className="atl-subtitle">{displayName}</span>
          </div>
          <button className="atl-close-btn" onClick={onClose}>
            <HiOutlineX size={18} />
          </button>
        </div>

        <div className="atl-body">
          {/* Search */}
          <div className="atl-search-wrapper">
            <HiOutlineSearch className="atl-search-icon" size={16} />
            <input
              type="text"
              className="atl-search-input"
              placeholder="Search lists..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="atl-loading">Loading lists...</div>
          ) : (
            <>
              {filteredLists.length > 0 && (
                <div className="atl-list-group">
                  {filteredLists.map(list => {
                    const isChecked = selectedLists.has(list.list_name);
                    return (
                      <label key={list.list_name} className={`atl-list-option ${isChecked ? 'selected' : ''}`}>
                        <HiOutlineFolder className="atl-folder-icon" size={18} />
                        <div className="atl-list-info">
                          <span className="atl-list-name">{list.list_name}</span>
                          <span className="atl-list-count">{list.count} item{list.count !== 1 ? 's' : ''}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleList(list.list_name)}
                        />
                      </label>
                    );
                  })}
                </div>
              )}

              {filteredLists.length === 0 && lists.length > 0 && (
                <div className="atl-empty">No lists match your search.</div>
              )}

              {lists.length === 0 && !showNewListInput && (
                <div className="atl-empty">No lists yet. Create your first list below.</div>
              )}

              {showNewListInput ? (
                <div className="atl-new-list-input-row">
                  <input
                    ref={newListInputRef}
                    type="text"
                    className="atl-new-list-input"
                    placeholder="Enter list name..."
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateNewList();
                      if (e.key === 'Escape') setShowNewListInput(false);
                    }}
                  />
                  <button
                    className="atl-confirm-btn"
                    onClick={handleCreateNewList}
                    disabled={!newListName.trim()}
                  >
                    <HiOutlineCheck size={16} />
                  </button>
                </div>
              ) : (
                <button className="atl-create-list-btn" onClick={() => setShowNewListInput(true)}>
                  <HiOutlinePlus size={16} />
                  <span>Create New List</span>
                </button>
              )}
            </>
          )}
        </div>

        <div className="atl-footer">
          <button className="atl-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="atl-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
